import { Controller } from "@hotwired/stimulus"
import twZipcode from "../data/tw_zipcode.json"

export default class extends Controller {
  static targets = [
    "region", "taiwanFields", "overseasFields", "city", "zipCode",
    "overseasZipCode", "district", "country"
  ]

  static values = { citiesUrl: String }

  connect() {
    this.zipcodeData = twZipcode
    this.mainlandCities = [
      "基隆市", "台北市", "新北市", "宜蘭縣", "新竹市", "新竹縣",
      "桃園市", "苗栗縣", "台中市", "彰化縣", "南投縣", "嘉義市",
      "嘉義縣", "雲林縣", "台南市", "高雄市", "屏東縣", "台東縣", "花蓮縣"
    ]
    this.islandCities = ["金門縣", "連江縣", "澎湖縣"]
    this.setup(true)
  }

  disconnect() {
    this.citiesRequest?.abort()
  }

  setup(preserveValues = false) {
    const regionType = this.regionTarget.value || "TW_MAIN"
    const overseas = this.isOverseas(regionType)

    this.taiwanFieldsTargets.forEach(element => element.classList.toggle("hidden", overseas))
    this.overseasFieldsTargets.forEach(element => element.classList.toggle("hidden", !overseas))
    this.zipCodeTarget.disabled = overseas
    this.overseasZipCodeTarget.disabled = !overseas

    if (this.hasCountryTarget) {
      this.countryTarget.disabled = !overseas
    }

    if (overseas) {
      this.zipCodeTarget.innerHTML = '<option value="">請先選擇縣市</option>'
      this.updateDistrict()
      this.clearFieldError(this.zipCodeTarget)

      const countryCode = this.overseasCountryCode(regionType)
      const selectedCity = preserveValues ? this.cityTarget.dataset.savedCity : ""
      this.loadOverseasCityOptions(countryCode, selectedCity)
    } else {
      this.citiesRequest?.abort()
      this.cityTarget.disabled = false
      this.clearFieldError(this.overseasZipCodeTarget)

      const selectedCity = preserveValues ? this.cityTarget.dataset.savedCity : ""
      this.loadCityOptions(regionType, selectedCity)

      const selectedZipCode = preserveValues ? this.zipCodeTarget.dataset.savedZipCode : ""
      const selectedDistrict = preserveValues && this.hasDistrictTarget ? this.districtTarget.value : ""
      this.loadZipCodeOptions(selectedCity, selectedZipCode, selectedDistrict)
    }
  }

  toggleFields() {
    this.setup(false)
  }

  changeCountry() {
    this.loadOverseasCityOptions(this.countryTarget.value, "")
  }

  loadCityOptions(regionType, selectedCity = "") {
    const cities = this.isOutlying(regionType) ? this.islandCities : this.mainlandCities

    this.cityTarget.innerHTML = '<option value="">請選擇縣市</option>'
    cities.forEach(city => this.appendCityOption(city, selectedCity))
  }

  async loadOverseasCityOptions(countryCode, selectedCity = "") {
    this.citiesRequest?.abort()

    if (!countryCode) {
      this.cityTarget.innerHTML = '<option value="">請先選擇國家</option>'
      this.cityTarget.disabled = true
      return
    }

    this.cityTarget.innerHTML = '<option value="">城市載入中…</option>'
    this.cityTarget.disabled = true
    this.citiesRequest = new AbortController()

    try {
      const url = new URL(this.citiesUrlValue, window.location.origin)
      url.searchParams.set("country_code", countryCode)
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: this.citiesRequest.signal
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const cities = await response.json()
      this.cityTarget.innerHTML = '<option value="">請選擇城市</option>'
      cities.forEach(city => this.appendCityOption(city, selectedCity))
      this.cityTarget.disabled = false
    } catch (error) {
      if (error.name === "AbortError") return

      this.cityTarget.innerHTML = '<option value="">城市載入失敗，請重新選擇國家</option>'
      this.cityTarget.disabled = true
    }
  }

  appendCityOption(city, selectedCity) {
    const option = document.createElement("option")
    option.value = city
    option.textContent = city
    option.selected = city === selectedCity
    this.cityTarget.appendChild(option)
  }

  loadZipCodeOptions(selectedCity, selectedZipCode = "", selectedDistrict = "") {
    if (!selectedCity || !this.zipcodeData[selectedCity]) {
      this.zipCodeTarget.innerHTML = '<option value="">請先選擇縣市</option>'
      this.zipCodeTarget.disabled = true
      this.updateDistrict()
      return
    }

    const districtEntries = Object.entries(this.zipcodeData[selectedCity])
    const matchingDistricts = districtEntries.filter(([, zipcode]) => zipcode === selectedZipCode)

    this.zipCodeTarget.innerHTML = '<option value="">請選擇區域</option>'
    let hasSelectedZipCode = false

    if (selectedZipCode && !selectedDistrict && matchingDistricts.length > 1) {
      const option = document.createElement("option")
      option.value = selectedZipCode
      option.textContent = selectedZipCode
      option.selected = true
      this.zipCodeTarget.appendChild(option)
      hasSelectedZipCode = true
    }

    districtEntries.forEach(([district, zipcode]) => {
      const option = document.createElement("option")
      option.value = zipcode
      option.textContent = `${zipcode} ${district}`
      option.dataset.district = district

      if (
        zipcode === selectedZipCode &&
        (selectedDistrict ? district === selectedDistrict : matchingDistricts.length === 1) &&
        !hasSelectedZipCode
      ) {
        option.selected = true
        hasSelectedZipCode = true
      }

      this.zipCodeTarget.appendChild(option)
    })

    this.zipCodeTarget.disabled = false
    this.updateDistrict()
  }

  updateZipCodeOptions() {
    this.loadZipCodeOptions(this.cityTarget.value, "")
  }

  updateDistrict() {
    if (!this.hasDistrictTarget) return

    const selectedOption = this.zipCodeTarget.selectedOptions[0]
    this.districtTarget.value = selectedOption?.dataset.district || ""
  }

  overseasCountryCode(regionType) {
    if (regionType === "overseas") {
      return this.hasCountryTarget ? this.countryTarget.value : ""
    }

    return regionType
  }

  isOverseas(regionType) {
    return !["TW_MAIN", "TW_OUTLYING", "taiwan_mainland", "taiwan_islands"].includes(regionType)
  }

  isOutlying(regionType) {
    return ["TW_OUTLYING", "taiwan_islands"].includes(regionType)
  }

  clearFieldError(field) {
    field.classList.remove("is-invalid")
    const error = field.parentElement.querySelector("[data-validation-error]")
    if (!error) return

    error.textContent = ""
    error.classList.add("hidden")
  }
}
